import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import apiClient from "../api/apiClient";
import { ADMIN, OWNER } from "../constants/general";

import { useAuth } from "./AuthProvider";

const UserContext = createContext({
  loading: false,
  error: null,
  profile: null,
  roles: [],
  permissions: [],
  groups: [],
  name: "",
  email: "",
  networkUser: "",
  isAdmin: false,
  isOwner: false,
});

const getStringValue = (...values) => values.find((value) => typeof value === "string") || "";

const normalizeGroups = (groups) => {
  if (!Array.isArray(groups)) return [];

  return groups.map((group) => (typeof group === "string" ? group : group?.name)).filter(Boolean);
};

const normalizeUserProfile = (data, account) => {
  const payload = data?.user ?? data?.profile ?? data ?? {};
  const roles = Array.isArray(payload.roles) ? payload.roles : (data?.roles ?? []);
  const permissions = Array.isArray(payload.permissions)
    ? payload.permissions
    : (data?.permissions ?? []);
  const groups = normalizeGroups(
    payload.groups ?? data?.groups ?? payload.groupIds ?? data?.groupIds,
  );
  const name =
    getStringValue(payload.name, payload.displayName) ||
    `${payload.firstName || ""} ${payload.lastName || ""}`.trim();
  const email = getStringValue(
    payload.email,
    payload.mail,
    payload.userPrincipalName,
    account?.username,
  );
  const networkUser =
    getStringValue(payload.networkUser, payload.username, payload.userName, payload.login) ||
    email.split("@")[0];

  return {
    profile: payload,
    roles,
    permissions,
    groups,
    name,
    email,
    networkUser,
  };
};

export const UserProvider = ({ children }) => {
  const { isInitialized, isAuthenticated, account } = useAuth();
  const [state, setState] = useState({
    loading: false,
    error: null,
    profile: null,
    roles: [],
    permissions: [],
    groups: [],
    name: "",
    email: "",
    networkUser: "",
    isAdmin: false,
    isOwner: false,
  });

  useEffect(() => {
    if (!isInitialized) return;

    if (!isAuthenticated) {
      // eslint-disable-next-line no-console
      console.log("[user] not authenticated yet", { isInitialized, isAuthenticated, account });
      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        profile: null,
        roles: [],
        permissions: [],
        groups: [],
        name: "",
        email: "",
        networkUser: "",
        isAdmin: false,
        isOwner: false,
      }));

      return;
    }

    let isMounted = true;

    const loadUser = async () => {
      // eslint-disable-next-line no-console
      console.log("[user] loading /user/me", { isInitialized, isAuthenticated, account });
      // eslint-disable-next-line no-console
      console.trace("[user] apiClient.get /user/me");
      // eslint-disable-next-line no-console
      console.log("[user] apiClient info", {
        hasClient: Boolean(apiClient),
        hasGet: typeof apiClient?.get === "function",
        baseURL: process.env.REACT_APP_API_BASE_URL,
        adapterName: apiClient?.defaults?.adapter?.name,
      });
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // eslint-disable-next-line no-console
        console.trace("llameee al apii meeee");
        const response = await apiClient.get("/user/me");

        // eslint-disable-next-line no-console
        console.log("[user] /user/me response", response);
        const normalized = normalizeUserProfile(response?.data, account);
        const isAdmin = normalized.roles.includes(ADMIN);
        const isOwner = normalized.roles.includes(OWNER);

        if (!isMounted) return;

        // eslint-disable-next-line no-console
        console.log("[user] stored user data (/user/me)", {
          ...normalized,
          isAdmin,
          isOwner,
        });

        if (normalized.email) {
          localStorage.setItem("Email", normalized.email);
        }

        setState({
          ...normalized,
          isAdmin,
          isOwner,
          loading: false,
          error: null,
        });
      } catch (error) {
        if (!isMounted) return;

        // eslint-disable-next-line no-console
        console.error("[user] /user/me error", error);

        setState((prev) => ({
          ...prev,
          loading: false,
          error,
        }));
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, [account, isAuthenticated, isInitialized]);

  const value = useMemo(
    () => ({
      ...state,
      hasRole: (role) => state.roles.includes(role),
      hasPermission: (permission) => state.permissions.includes(permission),
    }),
    [state],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
  children: PropTypes.node,
};

export const useUser = () => useContext(UserContext);

export default UserProvider;
